import { setFxRates } from "../lib/fx";
import { tx } from "../lib/miniI18n";
import React, { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Search, Megaphone, Sparkles, Clock, Star, Flame, Trophy, Gift, ArrowDownToLine, UserCheck } from "lucide-react";
import api, { imgUrl } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useI18n } from "../i18n";
import { toast } from "sonner";
import DrawCard from "../components/DrawCard";
import DailyBonus from "../components/DailyBonus";
import SpinWheel from "../components/SpinWheel";
import { FestivalBanner } from "../components/FestivalBanner";
import { StoryBanner } from "../components/StoryBanner";
import { ProverbText } from "../components/ProverbText";
import { ProverbShareModal } from "../components/ProverbShareModal";
import { InstallBanner } from "../components/InstallBanner";
import { FirstPlayStrip } from "../components/FirstPlayStrip";
import { MiniGamesGrid, MiniTopStrip } from "../components/MiniGamesGrid";
import Countdown from "../components/Countdown";
import LuckyLive from "../components/LuckyLive";
import { TopBrands } from "../components/TopBrands";
import { BookPeek } from "../components/BookPeek";
import { InviteStrip } from "../components/InviteStrip";
import { ResultHourLineup } from "../components/ResultHourLineup";
import { MissionsCard } from "../components/Missions";
import { CardSkeletonList, EmptyState } from "../components/Common";
import { fmt0, to12h, firstDrawTime } from "../lib/format";
import { OfflineHint, useOffline } from "../components/OfflineHint";
import { RateNoticeBanner } from "../components/RateNoticeBanner";


export default function Home() {
  const navigate = useNavigate();
  const { user, wallet } = useAuth();
  const { t, lang } = useI18n();
  const bt = (b, k) => (lang !== "ta" && b.i18n?.[lang]?.[k]) || b[k];
  const [games, setGames] = useState([]);
  const [categories, setCategories] = useState(["All Draws"]);
  const [banners, setBanners] = useState([]);
  const touchX = useRef(0);
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cat, setCat] = useState("All Draws");
  const [search, setSearch] = useState("");
  const [favs, setFavs] = useState([]);
  const [slide, setSlide] = useState(0);
  const [proverbs, setProverbs] = useState(null);
  const [shareP, setShareP] = useState(null);
  const [frameIdx, setFrameIdx] = useState({});
  const onFrame = useCallback((id, f) => setFrameIdx((p) => (p[id] === f ? p : { ...p, [id]: f })), []);
  const rtl = ["ar", "ur"].includes(lang);

  useEffect(() => {
    api.get("/proverbs", { params: { lang } }).then((r) => setProverbs(r.data)).catch(() => setProverbs(null));
  }, [lang]);

  const cacheKey = `rl_home_cache_${user?.id || (localStorage.getItem("rl_token") ? "auth" : "guest")}`;
  const offline = useOffline();
  const [cachedAt, setCachedAt] = useState(null);
  const load = useCallback(async (silent = false) => {
    // Instant open + offline mode: paint from the last snapshot on this phone, then refresh from the server.
    let cached = null;
    try { cached = JSON.parse(localStorage.getItem(cacheKey) || "null"); } catch {}
    if (cached && silent !== true) { setGames(cached.games); setBanners(cached.banners); setConfig(cached.config); setFxRates(cached.config?.fx_rates); setCategories(cached.categories); setCachedAt(cached.at); setLoading(false); }
    else if (silent !== true) setLoading(true);
    try {
      const [g, b, c, cats] = await Promise.all([api.get("/games"), api.get("/banners"), api.get("/config"), api.get("/categories")]);
      setGames(g.data);
      setBanners(b.data);
      setConfig(c.data); setFxRates(c.data?.fx_rates);
      setCategories(cats.data);
      const at = Date.now(); setCachedAt(at);
      try { localStorage.setItem(cacheKey, JSON.stringify({ games: g.data, banners: b.data, config: c.data, categories: cats.data, at })); } catch {}
    } catch {}
    setLoading(false);
  }, [cacheKey]);

  const loadFavs = useCallback(async () => {
    if (!user) return;
    try {
      const { data } = await api.get("/favourites");
      setFavs(data);
    } catch {}
  }, [user]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadFavs(); }, [loadFavs]);

  useEffect(() => {
    if (banners.length < 2) return;
    const cur = banners[slide] || {};
    const hold = cur.frames?.length ? (cur.duration_ms || 12000) + 800 : (cur.duration_ms || 4000);
    const t = setTimeout(() => setSlide((s) => (s + 1) % banners.length), hold);
    return () => clearTimeout(t);
  }, [banners, slide]);

  const toggleFav = async (gameId) => {
    if (!user) { toast.info("Login to save favourites"); return; }
    try {
      const { data } = await api.post(`/favourites/${gameId}`);
      setFavs((prev) => (data.favourite ? [...prev, gameId] : prev.filter((f) => f !== gameId)));
    } catch {}
  };

  let filtered = games;
  if (cat !== "All Draws") filtered = filtered.filter((g) => g.category === cat);
  if (search) filtered = filtered.filter((g) => g.name.toLowerCase().includes(search.toLowerCase()));

  const inCat = (g) => cat === "All Draws" || g.category === cat;
  const favGames = games.filter((g) => favs.includes(g.id) && inCat(g));
  const featuredGames = games.filter((g) => g.featured && !favs.includes(g.id) && inCat(g));

  const catImages = config?.category_images || {};
  const luckyGame = games.find((g) => g.mode === "interval");
  const catTiles = [];
  const seen = {};
  for (const g of games) {
    const name = g.category || "Other";
    if (!seen[name]) { seen[name] = { name, count: 0, image: catImages[name] || "", times: [], open: false, interval: null }; catTiles.push(seen[name]); }
    seen[name].count += 1;
    seen[name].times.push(g.draw_time);
    if (g.mode === "interval") seen[name].interval = g;
    if (g.runtime?.is_open) seen[name].open = true;
  }
  const catRank = (n) => { const i = categories.indexOf(n); return i < 0 ? 999 : i; };
  catTiles.sort((a, b) => catRank(a.name) - catRank(b.name));

  return (
    <div className="space-y-4">
      {shareP && <ProverbShareModal proverb={shareP} label={proverbs?.label} rtl={rtl} onClose={() => setShareP(null)} />}
      {user?.role === "agent" && (
        <button onClick={() => navigate("/agent")} className="w-full rounded-2xl px-4 py-3 text-left text-white flex items-center justify-between active:scale-[0.98] transition-transform" style={{ background: "linear-gradient(120deg,#1d4ed8,#0f172a)" }} data-testid="home-agent-panel-btn">
          <div><div className="font-display font-black text-sm">AGENT PANEL →</div><div className="text-[10px] text-white/80">{lang === "ta" ? "Booking · Approvals · Bills · Share sheet" : "Booking · Approvals · Bills · Share sheet"}</div></div>
          <span className="rl-gold-bg text-[10px] font-black px-3 py-1.5 rounded-full">{lang === "ta" ? "திற" : "OPEN"}</span>
        </button>
      )}
      {/* Festival greeting banner */}
      {config?.active_festival && <FestivalBanner festival={config.active_festival} />}

      {/* Install app banner (dismissable) */}
      <InstallBanner />
      <FirstPlayStrip token={user?.id} />
      <OfflineHint offline={offline} at={cachedAt} ta={lang === "ta"} />
      <RateNoticeBanner notice={config?.rate_notice} ta={lang === "ta"} />
      {config?.agent_shows && (
        <div className="rounded-xl bg-amber-50 border border-amber-200 px-3 py-2 text-[11px] text-amber-900 flex items-center gap-2" data-testid="home-agent-shows-hint">
          <UserCheck className="w-3.5 h-3.5 shrink-0" />
          <span>{lang === "ta" ? <><b>உங்கள் ஏஜென்ட்</b> ({config.agent_shows.agent_name}) தேர்ந்த {config.agent_shows.count} shows மட்டும் இங்கே காட்டப்படும்.</> : <><b>Your agent's shows</b> — {config.agent_shows.agent_name} has enabled {config.agent_shows.count} shows for you.</>}</span>
        </div>
      )}

      {/* Royal Lucky Spin — FIRST thing customers see */}
      <SpinWheel />

      {/* Kerala · Dear · Golden Raja */}
      <TopBrands games={games} catImages={catImages} ta={lang === "ta"} />
      <InviteStrip ta={lang === "ta"} />

      {/* Banner slider */}
      {loading ? (
        <div className="rl-skeleton h-32 rounded-2xl" />
      ) : (
        <div className="relative h-40 rounded-2xl overflow-hidden shadow-lg" data-testid="banner-slider"
          onTouchStart={(e) => { touchX.current = e.touches[0].clientX; }}
          onTouchEnd={(e) => {
            const dx = e.changedTouches[0].clientX - touchX.current;
            if (Math.abs(dx) > 40) setSlide((s) => (s + (dx < 0 ? 1 : banners.length - 1)) % banners.length);
          }}>
          {banners.map((b, i) => {
            const pv = proverbs?.banners?.[b.id]?.[frameIdx[b.id] || 0];
            return (
            <div key={b.id} className={`absolute inset-0 flex flex-col justify-center transition-opacity duration-700 ${i === slide ? "opacity-100" : "opacity-0"}`}>
              {b.frames?.length ? <StoryBanner banner={b} active={i === slide} onFrame={(f) => onFrame(b.id, f)} /> : b.image && <img src={imgUrl(b.image)} alt={b.title} className="absolute inset-0 w-full h-full object-cover" />}
              <div className="absolute inset-0 p-5 flex flex-col justify-center" style={{ background: b.image ? (rtl ? "linear-gradient(270deg, rgba(0,0,0,0.6), rgba(0,0,0,0.05))" : "linear-gradient(90deg, rgba(0,0,0,0.6), rgba(0,0,0,0.05))") : `linear-gradient(120deg, ${b.color} 0%, #1B263B 100%)` }}>
                {pv ? <button onClick={() => setShareP(pv)} className="text-left w-full active:scale-[0.99] transition-transform" data-testid="banner-proverb-tap"><ProverbText proverb={pv} label={proverbs?.label} rtl={rtl} /><span className="mt-1.5 inline-flex items-center gap-1 text-[9px] font-bold text-white/70 bg-white/10 border border-white/20 rounded-full px-2 py-0.5">↗ {tx("Share")}</span></button> : (
                  <>
                    <span className="rl-gold-bg text-[10px] font-bold px-2 py-0.5 rounded-full w-fit mb-1">{bt(b, "badge")}</span>
                    <h3 className="font-display font-black text-white text-lg leading-tight" data-testid="banner-title">{bt(b, "title")}</h3>
                    <p className="text-blue-100 text-xs mt-0.5">{bt(b, "subtitle")}</p>
                  </>
                )}
              </div>
            </div>
            );
          })}
          <div className="absolute bottom-2 left-0 right-0 flex justify-center gap-1.5">
            {banners.map((_, i) => (
              <span key={i} className={`h-1.5 rounded-full transition-all ${i === slide ? "w-5 bg-blue-600" : "w-1.5 bg-white/30"}`} />
            ))}
          </div>
        </div>
      )}

      {/* Lucky — 30-minute auto draw */}
      {luckyGame && (
        <button onClick={() => navigate(`/play/${luckyGame.id}`)} className="w-full rounded-2xl p-3 flex items-center gap-3 text-left text-white shadow-lg active:scale-[0.98] transition-transform relative overflow-hidden" style={{ background: "linear-gradient(120deg,#7c2d12,#1e1b4b)" }} data-testid="lucky-featured-card">
          <span className="absolute -right-6 -top-6 w-24 h-24 rounded-full bg-amber-400/20 blur-2xl" />
          {catImages[luckyGame.category] ? <img src={imgUrl(catImages[luckyGame.category])} alt={luckyGame.name} className="w-14 h-14 rounded-xl object-cover rl-gold-border" /> : <div className="w-14 h-14 rounded-xl rl-gold-bg flex items-center justify-center font-display font-black text-xl text-slate-900">L</div>}
          <div className="flex-1 min-w-0">
            <div className="text-[9px] uppercase tracking-widest text-amber-200">⚡ Every {luckyGame.runtime?.interval_minutes || 30} min · {to12h(firstDrawTime(luckyGame))}–{to12h(luckyGame.runtime?.last_draw_time || luckyGame.draw_time)}</div>
            <div className="font-display font-black text-base leading-tight">{luckyGame.name}</div>
            <div className="text-[11px] text-amber-100" data-testid="lucky-next-draw">
              {luckyGame.runtime?.is_open ? <>{luckyGame.runtime.pending_slot && <span className="text-amber-200" data-testid="home-lucky-pending">{to12h(luckyGame.runtime.pending_slot)} closed · result in <Countdown seconds={luckyGame.runtime.pending_draw_seconds} className="text-amber-300 font-bold" /> · </span>}Booking now for <b className="text-white bg-white/20 px-1 rounded" data-testid="home-lucky-booking-slot">{to12h(luckyGame.runtime.draw_slot)}</b> · closes in <Countdown seconds={luckyGame.runtime.closing_seconds} className="text-amber-300 font-bold" onExpire={() => load(true)} /></> : "Closed for today · opens 9 AM"}
              {luckyGame.last_result && <> · Last <b className="text-amber-300">{luckyGame.last_result.winning_number}</b></>}
            </div>
          </div>
          <span className="rl-gold-bg text-[10px] font-black px-3 py-1.5 rounded-full">{luckyGame.runtime?.is_open ? "PLAY" : "VIEW"}</span>
        </button>
      )}

      {luckyGame && <LuckyLive game={luckyGame} compact onSettled={() => load(true)} />}
      <MiniTopStrip />
      <ResultHourLineup games={games} catImages={catImages} onSettled={() => load(true)} />

      {config?.announcement && (
        <div className="rl-card px-3 py-2 flex items-center gap-2 overflow-hidden" data-testid="announcement-ticker">
          <Megaphone className="w-4 h-4 text-blue-600 shrink-0" />
          <div className="overflow-hidden flex-1">
            <div className="rl-ticker text-xs text-blue-700">{config.announcement} &nbsp;&nbsp;•&nbsp;&nbsp; {config.announcement}</div>
          </div>
        </div>
      )}

      {/* Quick promos */}
      <div className="grid grid-cols-3 gap-2" data-testid="home-promos">
        <button onClick={() => navigate("/recharge")} data-testid="promo-recharge" className="rounded-2xl p-3 text-left text-white active:scale-95 transition-transform" style={{ background: "linear-gradient(135deg,#2563EB,#1D4ED8)" }}>
          <ArrowDownToLine className="w-5 h-5 mb-1.5" /><div className="text-xs font-bold leading-tight">{t("recharge")}</div>
        </button>
        <button onClick={() => navigate("/gift-code")} data-testid="promo-gift" className="rounded-2xl p-3 text-left text-white active:scale-95 transition-transform" style={{ background: "linear-gradient(135deg,#F43F5E,#E11D48)" }}>
          <Gift className="w-5 h-5 mb-1.5" /><div className="text-xs font-bold leading-tight">{t("gift_code")}</div>
        </button>
        <button onClick={() => navigate("/leaderboard")} data-testid="promo-ranking" className="rounded-2xl p-3 text-left text-white active:scale-95 transition-transform" style={{ background: "linear-gradient(135deg,#F59E0B,#D97706)" }}>
          <Trophy className="w-5 h-5 mb-1.5" /><div className="text-xs font-bold leading-tight">{t("ranking")}</div>
        </button>
      </div>

      {/* Daily bonus */}
      <DailyBonus />

      {/* Instant mini games */}
      <MiniGamesGrid compact />
      <MissionsCard />

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3.5 top-3.5 w-4 h-4 text-slate-500" />
        <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder={t("search_games")}
          className="w-full rl-card pl-11 pr-4 py-3 text-slate-900 placeholder-slate-400 outline-none text-sm" data-testid="search-input" />
      </div>

      {/* Categories */}
      <div className="flex gap-2 overflow-x-auto pb-1 -mx-3 px-3">
        {categories.map((c) => (
          <button key={c} onClick={() => setCat(c)} data-testid={`category-${c.replace(/\s/g, "-")}`}
            className={`whitespace-nowrap px-4 py-2 rounded-full text-xs font-semibold shrink-0 ${cat === c ? "rl-gold-bg" : "rl-card text-slate-600"}`}>
            {c}
          </button>
        ))}
      </div>

      {/* Most played — featured shows (Golden Raja / Dear / Kerala) lifted out of the general list */}
      {featuredGames.length > 0 && !search && (
        <div className="rounded-3xl p-3 -mx-1 bg-gradient-to-b from-amber-100 via-amber-50 to-transparent border border-amber-200" data-testid="home-featured">
          <div className="flex items-center justify-between mb-2 px-1">
            <h2 className="font-display font-black text-slate-900 flex items-center gap-2"><Flame className="w-4 h-4 text-orange-500" />{lang === "ta" ? "அதிகம் விளையாடும் shows" : "Most played shows"}</h2>
            <span className="text-[10px] font-black px-2 py-0.5 rounded-full bg-orange-500 text-white animate-pulse" data-testid="home-featured-badge">🔥 HOT</span>
          </div>
          <div className="space-y-3">
            {featuredGames.map((g) => <DrawCard key={g.id} game={g} isFav={false} onToggleFav={toggleFav} />)}
          </div>
        </div>
      )}

      {/* Favourites */}
      {favGames.length > 0 && (
        <div>
          <h2 className="font-display font-bold text-slate-900 flex items-center gap-2 mb-2"><Star className="w-4 h-4 text-blue-600 fill-blue-600" />{t("favourites")}</h2>
          <div className="space-y-3">
            {favGames.map((g) => <DrawCard key={g.id} game={g} isFav onToggleFav={toggleFav} />)}
          </div>
        </div>
      )}

      {/* Games */}
      <div>
        {loading ? (
          <CardSkeletonList count={4} />
        ) : (cat === "All Draws" && !search) ? (
          <>
            <h2 className="font-display font-bold text-slate-900 flex items-center gap-2 mb-3"><Flame className="w-4 h-4 text-blue-600" />{t("game_categories")}</h2>
            <div className="grid grid-cols-2 gap-3" data-testid="category-grid">
              {catTiles.map((c) => (
                <button key={c.name} onClick={() => navigate(`/category/${encodeURIComponent(c.name)}`)} data-testid={`cat-tile-${c.name.replace(/\s/g, "-")}`}
                  className="relative rounded-2xl overflow-hidden shadow-md active:scale-95 transition-transform aspect-square rl-gold-border">
                  {c.image ? <img src={imgUrl(c.image)} alt={c.name} className="absolute inset-0 w-full h-full object-cover" /> : <div className="absolute inset-0 rl-gold-bg" />}
                  <BookPeek className="absolute top-2 right-2 opacity-75 bg-black/25 backdrop-blur-[2px] rounded-lg px-1.5 py-1" tone="light" testId={`book-peek-tile-${c.name.replace(/\s/g, "-")}`} />
                  {c.open && (
                    <span className="absolute top-2 left-2 flex items-center gap-1 bg-emerald-500 text-white text-[9px] font-black px-2 py-0.5 rounded-full shadow" data-testid={`cat-open-${c.name.replace(/\s/g, "-")}`}>
                      <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" />OPEN
                    </span>
                  )}
                  <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/85 via-black/50 to-transparent p-2 pt-8 text-left">
                    <div className="text-white font-display font-black text-sm leading-tight drop-shadow">{c.name}</div>
                    <div className="text-[10px] text-amber-300 font-semibold">{c.interval ? <>⚡ Every {c.interval.runtime?.interval_minutes || 30} min · {to12h(firstDrawTime(c.interval))}–{to12h(c.interval.runtime?.last_draw_time || c.interval.draw_time)}</> : <>{c.count} show{c.count !== 1 ? "s" : ""} · {c.times.slice(0, 4).map(to12h).join(" · ")}</>}</div>
                    <div className="mt-1 inline-flex items-center gap-1 rl-gold-bg text-[9px] font-black px-2 py-0.5 rounded-full">BOOK NOW ›</div>
                  </div>
                </button>
              ))}
            </div>
          </>
        ) : filtered.length === 0 ? (
          <EmptyState icon={Sparkles} title={t("no_games_found")} subtitle={t("try_different")} />
        ) : (
          <>
            <h2 className="font-display font-bold text-slate-900 flex items-center gap-2 mb-2"><Flame className="w-4 h-4 text-blue-600" />{cat === "All Draws" ? t("results") : cat}</h2>
            <div className="space-y-3">
              {filtered.map((g) => <DrawCard key={g.id} game={g} isFav={favs.includes(g.id)} onToggleFav={toggleFav} />)}
            </div>
          </>
        )}
      </div>

      <p className="text-center text-[10px] text-slate-500 pt-2 pb-4">18+ Only · Play Responsibly</p>
    </div>
  );
}
